import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Trash2, Loader2, ShieldAlert, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AccountPanel() {
  const { user, logout } = useAuth();
  const [account, setAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/account");
        setAccount(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const save = async () => {
    if (!account) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/account", {
        name: account.name,
        billing_email: account.billing_email,
        phone: account.phone,
        gstin: account.gstin,
        address: account.address,
      });
      setAccount(data);
      toast.success("Account updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fueltrack-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", { confirm_email: confirmEmail });
      toast.success("Your account has been permanently deleted");
      await logout();
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  if (!account) return <div className="text-[#737373] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  const isOwner = user?.role === "account_owner";
  const isDefault = account.id === "default";

  return (
    <div className="space-y-8" data-testid="account-panel">
      <div>
        <p className="ft-overline">TENANT · ACCOUNT PROFILE</p>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <Building2 size={22} className="text-[#FF5A1F]" /> Your Account
        </h2>
        <p className="text-sm text-[#A3A3A3] mt-1">Plan: <span className="text-white font-mono font-bold uppercase">{account.plan_id}</span> · Status: <span className="text-emerald-400 font-mono uppercase">{account.status}</span></p>
      </div>

      <div className="ft-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Account name" value={account.name} onChange={(v) => setAccount({ ...account, name: v })} testid="acct-name" />
          <Field label="Billing email" value={account.billing_email || ""} onChange={(v) => setAccount({ ...account, billing_email: v })} testid="acct-billing" />
          <Field label="Phone" value={account.phone || ""} onChange={(v) => setAccount({ ...account, phone: v })} testid="acct-phone" />
          <Field label="GSTIN (optional)" value={account.gstin || ""} onChange={(v) => setAccount({ ...account, gstin: v })} testid="acct-gstin" />
          <div className="md:col-span-2">
            <Field label="Address" value={account.address || ""} onChange={(v) => setAccount({ ...account, address: v })} testid="acct-address" multiline />
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving || !user?.is_admin}
          className="bg-[#FF5A1F] text-black font-bold px-5 py-2 hover:bg-[#E04F1A] disabled:opacity-60 inline-flex items-center gap-2"
          data-testid="acct-save"
        >
          {saving && <Loader2 size={14} className="animate-spin" />} Save changes
        </button>
      </div>

      <div className="ft-card p-6 space-y-3">
        <p className="ft-overline">DATA PORTABILITY (DPDP)</p>
        <p className="text-sm text-[#A3A3A3] max-w-2xl">
          Download a complete JSON export of every user, vehicle, refuel, service and document belonging to this account.
          Useful for backups or if you want to migrate elsewhere.
        </p>
        <button
          onClick={exportData}
          disabled={exporting}
          className="border border-[#262626] px-4 py-2 font-semibold hover:bg-[#141414] hover:border-[#FF5A1F]/50 inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="acct-export"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export my data
        </button>
      </div>

      {isOwner && !isDefault && (
        <div className="ft-card p-6 space-y-3 border-l-4 border-red-500/60">
          <p className="ft-overline text-red-400 flex items-center gap-2"><ShieldAlert size={14} /> DANGER ZONE</p>
          <p className="text-sm text-[#A3A3A3] max-w-2xl">
            Permanently delete this account and every piece of data associated with it. This cannot be undone.
          </p>
          {!showDelete ? (
            <button
              onClick={() => setShowDelete(true)}
              className="border border-red-500/40 text-red-300 px-4 py-2 font-semibold hover:bg-red-500/10 inline-flex items-center gap-2"
              data-testid="acct-delete-open"
            >
              <Trash2 size={14} /> Close my account
            </button>
          ) : (
            <div className="border border-red-500/40 p-4 bg-red-500/5 space-y-3">
              <p className="text-sm">Type your login email <b>{user?.email}</b> to confirm:</p>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-red-500/40 px-3 py-2 font-mono text-white focus:outline-none"
                data-testid="acct-delete-confirm"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={doDelete}
                  disabled={deleting || confirmEmail.toLowerCase() !== (user?.email || "").toLowerCase()}
                  className="bg-red-500 text-white font-bold px-4 py-2 hover:bg-red-600 disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="acct-delete-submit"
                >
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Permanently delete
                </button>
                <button
                  onClick={() => { setShowDelete(false); setConfirmEmail(""); }}
                  className="text-[#A3A3A3] hover:text-white px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, multiline }) {
  return (
    <div>
      <label className="ft-overline block mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:border-[#FF5A1F] outline-none"
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 font-mono text-white focus:border-[#FF5A1F] outline-none"
          data-testid={testid}
        />
      )}
    </div>
  );
}
